import { useEffect, useState, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { garageCarUrl } from '../lib/slugId';
import { PageHeader } from '../components/PageHeader';
import { FormLayout } from '../components/FormLayout';
import { PhotoGalleryField } from '../components/PhotoGalleryField';
import { MarkdownEditor } from '../components/MarkdownEditor';
import type { Car, Garage, Paginated, SingleResponse } from '../api/types';

export function EditCar() {
  const { slugId } = useParams<{ slugId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [nickname, setNickname] = useState('');
  const [intro, setIntro] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState<number | ''>('');
  const [summary, setSummary] = useState('');
  const [garageId, setGarageId] = useState<number | ''>('');
  const [garages, setGarages] = useState<Garage[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
  const [newPhotos, setNewPhotos] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!slugId) return;
    apiFetch<SingleResponse<Car>>(`/api/v1/cars/${slugId}`)
      .then((res) => {
        const car = res.data;
        if (user && car.user_id !== user.id) {
          setError(t('editCar.notOwner'));
          return;
        }
        setNickname(car.nickname ?? '');
        setIntro(car.intro ?? '');
        setMake(car.make);
        setModel(car.model);
        setYear(car.year);
        setSummary(car.summary ?? '');
        setExistingPhotos(car.photo_urls);
        setGarageId(car.garage_id ?? '');
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slugId, user, t]);

  useEffect(() => {
    if (!user) return;
    apiFetch<Paginated<Garage>>('/api/v1/garages?per_page=50')
      .then((res) => setGarages(res.data))
      .catch(() => setGarages([]));
  }, [user]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('_method', 'PUT');
      form.append('nickname', nickname.trim());
      form.append('intro', intro);
      form.append('make', make);
      form.append('model', model);
      form.append('year', String(year));
      form.append('summary', summary);
      if (garageId !== '') form.append('garage_id', String(garageId));
      existingPhotos.forEach((url) => form.append('photo_urls[]', url));
      newPhotos.forEach((file) => form.append('photos[]', file));

      const res = await apiFetch<SingleResponse<Car>>(`/api/v1/cars/${slugId}`, {
        method: 'POST', // POST + _method=PUT for multipart
        body: form,
      });
      // Use the response's canonical slug — the nickname/model edit may have
      // changed it, so we navigate to the fresh URL rather than the stale slugId.
      // Owner edits land back in the garage dashboard, not the public car page.
      navigate(garageCarUrl(res.data));
    } catch (err: any) {
      setError(err.message ?? t('editCar.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiFetch(`/api/v1/cars/${slugId}`, { method: 'DELETE' });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message ?? t('editCar.deleteFailed'));
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <FormLayout
        header={<PageHeader />}
        title={t('editCar.title')}
        error={error || null}
        form={
          <>
            <TextField
              label={t('newCar.nickname')}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              helperText={t('newCar.nicknameHint')}
              fullWidth
            />
            <TextField
              label={t('newCar.intro')}
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('newCar.make')}
                value={make}
                onChange={(e) => setMake(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label={t('newCar.model')}
                value={model}
                onChange={(e) => setModel(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label={t('newCar.year')}
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value ? parseInt(e.target.value, 10) : '')}
                required
                sx={{ minWidth: 110 }}
              />
            </Stack>
          </>
        }
        aside={
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel id="car-garage-label">{t('editCar.garage')}</InputLabel>
              <Select
                labelId="car-garage-label"
                label={t('editCar.garage')}
                value={garageId}
                onChange={(e) => setGarageId(Number(e.target.value))}
              >
                {garages.map((garage) => (
                  <MenuItem key={garage.id} value={garage.id}>
                    {garage.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <MarkdownEditor
              label={t('newCar.summary')}
              value={summary}
              onChange={setSummary}
              rows={6}
            />
            <PhotoGalleryField
              label={t('editCar.photosLabel')}
              addButtonLabel={t('editCar.addMorePhotos')}
              existingPhotos={existingPhotos}
              onRemoveExisting={(url) =>
                setExistingPhotos((prev) => prev.filter((u) => u !== url))
              }
              newPhotos={newPhotos}
              onAdd={(files) => setNewPhotos((prev) => [...prev, ...files])}
              onRemoveNew={(idx) => setNewPhotos((prev) => prev.filter((_, i) => i !== idx))}
            />
          </Stack>
        }
        footer={
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
          >
            <Button
              variant="text"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmDelete(true)}
            >
              {t('editCar.deleteCar')}
            </Button>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => navigate(`/garage/${slugId}`)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? t('common.uploading') : t('common.save')}
              </Button>
            </Stack>
          </Stack>
        }
      />

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>{t('editCar.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('editCar.confirmDeleteBody', { nickname: nickname.trim() || `${make} ${model}` })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? t('editCar.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
